import { createEmptyCondition, isConditionGroup } from '@src/components/catalog/CatalogRules/types';
import type { ConditionItem } from '@src/components/catalog/CatalogRules/types';
import type { CatalogTemplateDefinitions } from '@src/settings';

/** Properties backed by a `catalog_item_aliases` column are external identifiers; everything else is a regular field. */
const IDENTIFIER_COLUMN_PREFIX = 'catalog_item_aliases.';

/**
 * The upload validator requires a `sku` column on every upload regardless of what the partner
 * selects — it isn't part of any partner's `catalogTemplateDefinitions` schema, so the wizard
 * always shows it as an always-selected, locked "Trove SKU" field (see `withTroveSkuField` below).
 */
export const TROVE_SKU_KEY = 'Trove SKU';
const TROVE_SKU_CSV_COLUMN = 'sku';
const TROVE_SKU_SOURCE_COLUMN = 'sku';

export type WizardStepKey = 'scope' | 'fields' | 'values' | 'review';

interface WizardStep {
  key: WizardStepKey;
  label: string;
}

/**
 * Whether the template is for brand-new catalog records or for updating existing ones — chosen
 * on the Scope step and used to gate which later questions/steps apply (DATAPROD-2677). A
 * "create" template has nothing to scope/match against or pre-fill from, so it skips the
 * scope-conditions UI and the Values step entirely.
 */
export type TemplatePurpose = 'create' | 'update';

/**
 * Step list depends on `purpose`: a "create" template has no existing records to pre-fill
 * values from, so it skips the Values step. External identifiers are selected in the "Values"
 * step (update only), alongside the pre-fill question.
 */
export function getWizardSteps(purpose: TemplatePurpose): WizardStep[] {
  const fieldsLabel = purpose === 'create' ? 'Fields to Include' : 'Fields to Update';
  if (purpose === 'create') {
    return [
      { key: 'scope', label: 'Scope' },
      { key: 'fields', label: fieldsLabel },
      { key: 'review', label: 'Review' },
    ];
  }
  return [
    { key: 'scope', label: 'Scope' },
    { key: 'fields', label: fieldsLabel },
    { key: 'values', label: 'Template Values' },
    { key: 'review', label: 'Review' },
  ];
}

export type ScopeMode = 'all' | 'subset';
export type ValuesMode = 'prefill' | 'blank';

export type CustomFieldSource = 'family' | 'data';

export interface CustomFieldSelection {
  id: string;
  family: CustomFieldSource;
  key: string;
}

export interface WizardState {
  purpose: TemplatePurpose;
  scopeMode: ScopeMode;
  conditions: ConditionItem[];
  selectedIdentifiers: string[];
  selectedFields: string[];
  customFields: CustomFieldSelection[];
  valuesMode: ValuesMode;
}

/** A rule is "defined" once it has a `field` set, or (for a group) has at least one child. */
const isConditionDefined = (item: ConditionItem): boolean =>
  (isConditionGroup(item) ? item.children.length > 0 : !!item.field);

/**
 * Whether the scope conditions include at least one actually-defined rule — the default empty
 * condition doesn't count.
 */
export const hasAnyConditionSet = (state: WizardState): boolean =>
  state.conditions.some(isConditionDefined);

/**
 * Top-level conditions that are actually defined, dropping any still-blank rule (e.g. a second
 * "Add Rule" row the partner hasn't filled in yet). The backend rejects a condition that has
 * neither a `field` nor a non-empty `conditions` array, so callers must send this — not the raw
 * `state.conditions` — to `catalogItemsForTemplate`.
 */
export const getDefinedConditions = (state: WizardState): ConditionItem[] =>
  state.conditions.filter(isConditionDefined);

export const INITIAL_WIZARD_STATE: WizardState = {
  purpose: 'create',
  scopeMode: 'all',
  conditions: [createEmptyCondition()],
  selectedIdentifiers: [],
  selectedFields: [],
  customFields: [],
  valuesMode: 'prefill',
};

export interface FieldDefinition {
  /** Identity used for `selectedIdentifiers`/`selectedFields` membership. */
  key: string;
  /** Display label shown as the Fields to Update checkbox. */
  label: string;
  /** The CSV header this field is written under — always the human-readable name, never a raw DB column. */
  csvColumn: string;
  /**
   * DB column path(s) backing this field (`x-column`/`x-columns`), tried in order when looking
   * up a prefill value from the fetched catalog item row — first non-empty wins.
   */
  sourceColumns: string[];
  /**
   * Whether this column is in `catalogTemplateDefinitions.required` — the backend's upload
   * validator rejects a file missing a value here. There's no existing record to match against
   * on a "create new records" template, so those columns must be filled in directly: the wizard
   * pre-checks and locks them in that flow (see `FieldsToUpdateStep`). Not enforced this way for
   * "update", where the partner may legitimately be updating only a subset of fields.
   */
  required: boolean;
}

export interface FieldCatalog {
  identifiers: FieldDefinition[];
  fields: FieldDefinition[];
}

/**
 * Builds the wizard's selectable identifier/field catalog from the partner's
 * `catalogTemplateDefinitions` JSON Schema (DATAPROD-2677) — `properties` is the full set of
 * columns the backend accepts in an upload template. There is no separate hardcoded field list.
 */
export function buildFieldCatalog(definitions: CatalogTemplateDefinitions | null | undefined): FieldCatalog {
  const properties = definitions?.properties ?? {};
  const requiredKeys = new Set(definitions?.required ?? []);
  const catalog: FieldCatalog = { identifiers: [], fields: [] };

  for (const [label, property] of Object.entries(properties)) {
    const sourceColumns = property['x-columns'] ?? (property['x-column'] ? [property['x-column']] : []);
    const definition: FieldDefinition = {
      key: label,
      label,
      csvColumn: label,
      sourceColumns,
      required: requiredKeys.has(label),
    };
    const isIdentifier = sourceColumns.some(column => column.startsWith(IDENTIFIER_COLUMN_PREFIX));
    (isIdentifier ? catalog.identifiers : catalog.fields).push(definition);
  }

  return catalog;
}

/**
 * Prepends the always-selected, non-deselectable "Trove SKU" field to the catalog's Fields to
 * Update list — the upload validator requires a `sku` column that isn't part of any partner's
 * `catalogTemplateDefinitions`. Only meaningful for "update" templates, which match rows to
 * existing records by sku; a "create new records" template has no existing sku to match, so
 * callers should skip this for that flow (see `CreateCatalogUploadTemplateDialog`).
 */
export function withTroveSkuField(catalog: FieldCatalog): FieldCatalog {
  const troveSkuField: FieldDefinition = {
    key: TROVE_SKU_KEY,
    label: TROVE_SKU_KEY,
    csvColumn: TROVE_SKU_CSV_COLUMN,
    sourceColumns: [TROVE_SKU_SOURCE_COLUMN],
    required: true,
  };
  return { identifiers: catalog.identifiers, fields: [troveSkuField, ...catalog.fields] };
}

/**
 * Keys of the given definitions the partner's schema marks `required` — used to force-select+lock
 * them on a "create new records" template. Applied separately to `catalog.fields` and
 * `catalog.identifiers` since each backs a different `WizardState` selection array
 * (`selectedFields` vs. `selectedIdentifiers`).
 */
export function getRequiredKeys(definitions: FieldDefinition[]): string[] {
  return definitions.filter(d => d.required).map(d => d.key);
}

/**
 * Drops fields backed by one of a partner's "vital element" columns (e.g. `parent_sku`,
 * `color_code`) from the Fields to Update step on an "update" template — those columns compose
 * the record's identity rather than being updatable data, so offering them as update candidates
 * would be misleading. The record is still matched by Trove SKU regardless of this list, so the
 * locked Trove SKU field itself is never dropped even if its column happens to collide. Not
 * applied on "create", where they're ordinary (often required) columns like any other — see
 * `CreateCatalogUploadTemplateDialog`, which only calls this for `purpose === 'update'`.
 */
export function withoutVitalElementFields(catalog: FieldCatalog, vitalElementColumns: string[]): FieldCatalog {
  if (vitalElementColumns.length === 0) return catalog;
  const vitalColumns = new Set(vitalElementColumns);
  const isVital = (field: FieldDefinition) => field.sourceColumns.some(column => vitalColumns.has(column));
  return {
    identifiers: catalog.identifiers,
    fields: catalog.fields.filter(f => f.key === TROVE_SKU_KEY || !isVital(f)),
  };
}

/** Every DB column the wizard may ever need to fetch, across both identifiers and fields. */
export function getAllSourceColumns(catalog: FieldCatalog): string[] {
  const columns = new Set<string>();
  for (const definition of [...catalog.identifiers, ...catalog.fields]) {
    definition.sourceColumns.forEach(column => columns.add(column));
  }
  return Array.from(columns);
}
